// Sheet and list building blocks in the iPadOS "inset grouped" style (Apple HIG, Lists and tables;
// Toggles): rows that open another screen end in a chevron, settings are switches in list rows,
// and actions are plain tappable rows. Nothing here looks like a text field unless it is one.

import type { ComponentChildren } from "preact";
import { inkOn, readableOnWhite } from "../../layout/color.ts";
import { type Dialog, dialog } from "../state.ts";

export function Sheet(props: { title: string; back?: Dialog; wide?: boolean; children: ComponentChildren; footer?: ComponentChildren; busy?: boolean }) {
  return (
    <div class="modal-backdrop" onClick={(e) => e.target === e.currentTarget && !props.busy && (dialog.value = null)}>
      <div class={`sheet ${props.wide ? "wide" : ""}`} role="dialog" aria-label={props.title}>
        <div class="sheet-head">
          {props.back ? (
            <button type="button" class="link back" disabled={props.busy} onClick={() => (dialog.value = props.back ?? null)}>
              ‹ Menu
            </button>
          ) : (
            <span />
          )}
          <strong>{props.title}</strong>
          <button type="button" class="link done" disabled={props.busy} onClick={() => (dialog.value = null)}>
            Done
          </button>
        </div>
        <div class="sheet-body" tabIndex={-1}>{props.children}</div>
        {props.footer && <div class="sheet-foot">{props.footer}</div>}
      </div>
    </div>
  );
}

export function Group(props: { title?: string; footer?: ComponentChildren; children: ComponentChildren }) {
  return (
    <section class="group">
      {props.title && <h3 class="group-title">{props.title}</h3>}
      <div class="group-rows">{props.children}</div>
      {props.footer && <p class="group-foot">{props.footer}</p>}
    </section>
  );
}

/** A row that opens another screen: label, optional detail, chevron. */
export function NavRow(props: { label: string; icon?: ComponentChildren; detail?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" class="row nav" disabled={props.disabled} onClick={props.onClick}>
      {props.icon}
      <span class="row-label">{props.label}</span>
      {props.detail && <span class="row-detail">{props.detail}</span>}
      <span class="chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/** A row that does something at once (no chevron). */
export function ActionRow(props: { label: string; detail?: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" class="row action" disabled={props.disabled} onClick={props.onClick}>
      <span class="row-label">{props.label}</span>
      {props.detail && <span class="row-detail">{props.detail}</span>}
    </button>
  );
}

/** A read-only fact: label and value, not tappable. */
export function InfoRow(props: { label: string; detail: string }) {
  return (
    <div class="row">
      <span class="row-label">{props.label}</span>
      <span class="row-detail">{props.detail}</span>
    </div>
  );
}

/** A setting: label on the left, an on/off switch on the right; the whole row toggles it. */
export function SwitchRow(props: { label: string; detail?: string; color?: string; checked: boolean; onChange: (on: boolean) => void; disabled?: boolean }) {
  return (
    <label class={`row switch-row ${props.disabled ? "disabled" : ""}`}>
      {props.color && <span class="swatch" style={{ background: props.color }} aria-hidden="true" />}
      <span class="row-label">{props.label}</span>
      {props.detail && <span class="row-detail">{props.detail}</span>}
      <input
        type="checkbox"
        role="switch"
        class="switch"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange((e.target as HTMLInputElement).checked)}
      />
    </label>
  );
}

/**
 * Style variables for anything in a department or layer colour: `--c` the colour itself (fills,
 * borders, bars), `--on` readable text on a `--c` fill, `--ct` the colour darkened if needed so it
 * reads as text on white (WCAG AA, 4.5:1).
 */
export function chipColors(color: string): Record<string, string> {
  return { "--c": color, "--on": inkOn(color), "--ct": readableOnWhite(color) };
}
